/*
   Copyright 2026 kanreisa

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { IconName } from "@blueprintjs/core";

export const channelTypeMap = {
    GR: "地上",
    BS: "BS",
    CS: "CS",
    SKY: "SKY",
};

export const relatedItemTypeMap = {
    shared: "イベント共有",
    relay: "イベントリレー",
    movement: "イベント移動"
};

export const relatedItemTypeIconMap: Record<string, IconName> = {
    shared: "duplicate",
    relay: "one-to-one",
    movement: "flow-linear"
};

export const langMap = {
    jpn: "日本語",
    eng: "英語",
    deu: "ドイツ語",
    fra: "フランス語",
    ita: "イタリア語",
    rus: "ロシア語",
    zho: "中国語",
    kor: "韓国語",
    spa: "スペイン語",
    etc: "その他",
};

export const audioModeMap = {
    "00001": "モノラル",
    "00010": "デュアルモノ",
    "00011": "ステレオ",
    "00100": "2/1モード",
    "00101": "3/0モード",
    "00110": "2/2モード",
    "00111": "3/1モード",
    "01000": "3/2モード",
    "01001": "5.1ch",
    "01010": "3/3.1モード",
    "01011": "2/0/0-2/0/2-0.1モード",
    "01100": "5/2.1モード",
    "01101": "3/2/2.1モード",
    "01110": "2/0/0-3/0/2-0.1モード",
    "01111": "0/2/0-3/0/2-0.1モード",
    "10000": "2/0/0-3/2/3-0.2モード",
    "10001": "3/3/3-5/2/3-3/0/0.2モード",
};

export const ProgramAttributeMap = {
    字: "字幕放送", // 字幕放送
    新: "新番組", // 新番組
    初: "初回放送", // 初回放送
    終: "最終回", // 最終回
    再: "再放送", // 再放送
    デ: "番組連動データ放送", // 番組連動データ放送
    双: "双方向放送", // 双方向放送
    無: "無料放送", // 無料放送
    二: "二ヶ国語放送", // 二ヶ国語放送
    多: "音声多重放送", // 音声多重放送
    SS: "サラウンドステレオ", // サラウンドステレオ
    生: "生放送", // 生放送
    前: "前編", // 前編
    後: "後編", // 後編
    解: "音声解説", // 音声解説
    PPV: "PPV", // PPV
    手: "手話通訳放送", // 手話通訳放送
};

export const Genre1Map = {
    0x0: "ニュース／報道",
    0x1: "スポーツ",
    0x2: "情報／ワイドショー",
    0x3: "ドラマ",
    0x4: "音楽",
    0x5: "バラエティ",
    0x6: "映画",
    0x7: "アニメ／特撮",
    0x8: "ドキュメンタリー／教養",
    0x9: "劇場／公演",
    0xA: "趣味／教育",
    0xB: "福祉",
    0xC: "予備",
    0xD: "予備",
    0xE: "拡張",
    0xF: "その他",
};

export const Genre2Map = {
    0x00: "定時・総合",
    0x01: "天気",
    0x02: "特集・ドキュメント",
    0x03: "政治・国会",
    0x04: "経済・市況",
    0x05: "海外・国際",
    0x06: "解説",
    0x07: "討論・会談",
    0x08: "報道特番",
    0x09: "ローカル・地域",
    0x0A: "交通",
    0x0F: "その他",

    0x10: "スポーツニュース",
    0x11: "野球",
    0x12: "サッカー",
    0x13: "ゴルフ",
    0x14: "その他の球技",
    0x15: "相撲・格闘技",
    0x16: "オリンピック・国際大会",
    0x17: "マラソン・陸上・水泳",
    0x18: "モータースポーツ",
    0x19: "マリン・ウィンタースポーツ",
    0x1A: "競馬・公営競技",
    0x1F: "その他",

    0x20: "芸能・ワイドショー",
    0x21: "ファッション",
    0x22: "暮らし・住まい",
    0x23: "健康・医療",
    0x24: "ショッピング・通販",
    0x25: "グルメ・料理",
    0x26: "イベント",
    0x27: "番組紹介・お知らせ",
    0x2F: "その他",

    0x30: "国内ドラマ",
    0x31: "海外ドラマ",
    0x32: "時代劇",
    0x3F: "その他",

    0x40: "国内ロック・ポップス",
    0x41: "海外ロック・ポップス",
    0x42: "クラシック・オペラ",
    0x43: "ジャズ・フュージョン",
    0x44: "歌謡曲・演歌",
    0x45: "ライブ・コンサート",
    0x46: "ランキング・リクエスト",
    0x47: "カラオケ・のど自慢",
    0x48: "民謡・邦楽",
    0x49: "童謡・キッズ",
    0x4A: "民族音楽・ワールドミュージック",
    0x4F: "その他",

    0x50: "クイズ",
    0x51: "ゲーム",
    0x52: "トークバラエティ",
    0x53: "お笑い・コメディ",
    0x54: "音楽バラエティ",
    0x55: "旅バラエティ",
    0x56: "料理バラエティ",
    0x5F: "その他",

    0x60: "洋画",
    0x61: "邦画",
    0x62: "アニメ",
    0x6F: "その他",

    0x70: "国内アニメ",
    0x71: "海外アニメ",
    0x72: "特撮",
    0x7F: "その他",

    0x80: "社会・時事",
    0x81: "歴史・紀行",
    0x82: "自然・動物・環境",
    0x83: "宇宙・科学・医学",
    0x84: "カルチャー・伝統文化",
    0x85: "文学・文芸",
    0x86: "スポーツ",
    0x87: "ドキュメンタリー全般",
    0x88: "インタビュー・討論",
    0x8F: "その他",

    0x90: "現代劇・新劇",
    0x91: "ミュージカル",
    0x92: "ダンス・バレエ",
    0x93: "落語・演芸",
    0x94: "歌舞伎・古典",
    0x9F: "その他",

    0xA0: "旅・釣り・アウトドア",
    0xA1: "園芸・ペット・手芸",
    0xA2: "音楽・美術・工芸",
    0xA3: "囲碁・将棋",
    0xA4: "麻雀・パチンコ",
    0xA5: "車・オートバイ",
    0xA6: "コンピュータ・ＴＶゲーム",
    0xA7: "会話・語学",
    0xA8: "幼児・小学生",
    0xA9: "中学生・高校生",
    0xAA: "大学生・受験",
    0xAB: "生涯教育・資格",
    0xAC: "教育問題",
    0xAF: "その他",

    0xB0: "高齢者",
    0xB1: "障害者",
    0xB2: "社会福祉",
    0xB3: "ボランティア",
    0xB4: "手話",
    0xB5: "文字（字幕）",
    0xB6: "音声解説",
    0xBF: "その他",

    0xC0: "予備",
    0xD0: "予備",

    0xE0: "BS/地上デジタル放送用番組付属情報",
    0xE1: "広帯域CSデジタル放送用拡張",
    0xE2: "衛星デジタル音声放送用拡張",
    0xE3: "サーバー型番組付属情報",
    0xE4: "IP放送用番組付属情報",
    0xF0: "その他",
    0xFF: "その他",
};

export const GenreUN1Map = {
    0xE10: "スポーツ",
    0xE11: "洋画",
    0xE12: "邦画",
};

export const GenreUN2Map = {
    0xE000: "中止の可能性あり",
    0xE001: "延長の可能性あり",
    0xE002: "中断の可能性あり",
    0xE003: "同一シリーズの別話数放送の可能性あり",
    0xE004: "編成未定枠",
    0xE005: "繰り上げの可能性あり",

    0xE010: "中断ニュースあり",
    0xE011: "当該イベントに関連する臨時サービスあり",
    0xE020: "当該イベント中に3D映像あり",

    0xE100: "テニス",
    0xE101: "バスケットボール",
    0xE102: "ラグビー",
    0xE103: "アメリカンフットボール",
    0xE104: "ボクシング",
    0xE105: "プロレス",
    0xE10F: "その他",

    0xE110: "アクション",
    0xE111: "ＳＦ／ファンタジー",
    0xE112: "コメディー",
    0xE113: "サスペンス／ミステリー",
    0xE114: "恋愛／ロマンス",
    0xE115: "ホラー／スリラー",
    0xE116: "ウエスタン",
    0xE117: "ドラマ／社会派ドラマ",
    0xE118: "アニメーション",
    0xE119: "ドキュメンタリー",
    0xE11A: "アドベンチャー／冒険",
    0xE11B: "ミュージカル／音楽映画",
    0xE11C: "ホームドラマ",
    0xE11F: "その他",

    0xE120: "アクション",
    0xE121: "ＳＦ／ファンタジー",
    0xE122: "お笑い／コメディー",
    0xE123: "サスペンス／ミステリー",
    0xE124: "恋愛／ロマンス",
    0xE125: "ホラー／スリラー",
    0xE126: "青春／学園／アイドル",
    0xE127: "任侠／時代劇",
    0xE128: "アニメーション",
    0xE129: "ドキュメンタリー",
    0xE12A: "アドベンチャー／冒険",
    0xE12B: "ミュージカル／音楽映画",
    0xE12C: "ホームドラマ",
    0xE12F: "その他",
};
